import React from 'react';
import {
  VictoryChart,
  VictoryArea,
  VictoryAxis,
  VictoryTheme,
  VictoryTooltip,
  VictoryLegend
} from 'victory';
import { formatPercentageES, formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 11: Liquidez General
 *
 * Area chart showing liquidity indicators (L1, L2, L3)
 */

export interface LiquidityIndicatorData {
  period: string;
  L1?: number;  // Liquid Investments Ratio
  L2?: number;  // Available Funds / Total Deposits
  L3?: number;  // Structural Liquidity
}

export interface Grafico11Props {
  data: LiquidityIndicatorData[];
  title?: string;
}

const INDICATOR_COLORS = {
  L1: COLORS.secondary,  // Teal (primary indicator)
  L2: COLORS.primary,    // Dark gray
  L3: '#9e9e9e',         // Medium gray
};

const INDICATOR_NAMES = {
  L1: 'Inversiones Líquidas',
  L2: 'Fondos Disponibles/Depósitos',
  L3: 'Liquidez Estructural',
};

export const Grafico11: React.FC<Grafico11Props> = ({
  data,
  title = 'Gráfico 11: Liquidez General'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Transform data for each indicator
  const indicators = ['L1', 'L2', 'L3'] as const;
  const chartDatasets = indicators.map(indicator => ({
    indicator,
    name: INDICATOR_NAMES[indicator],
    color: INDICATOR_COLORS[indicator],
    data: sortedData
      .filter(d => d[indicator] !== undefined && d[indicator] !== null)
      .map(d => ({
        x: formatPeriodES(d.period),
        y: d[indicator]! * 100,
        // Store metadata for tooltip
        indicatorName: INDICATOR_NAMES[indicator],
        period: formatPeriodES(d.period),
        value: formatPercentageES(d[indicator]! * 100)
      }))
  })).filter(ds => ds.data.length > 0);

  if (chartDatasets.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos de liquidez disponibles</p>
      </div>
    );
  }

  // Calculate global y-axis domain
  const allValues = chartDatasets.flatMap(ds => ds.data.map(d => d.y));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const padding = (maxValue - minValue) * 0.1;
  const yDomain: [number, number] = [
    Math.max(0, minValue - padding),
    maxValue + padding
  ];

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 20 }}
          padding={{ top: 40, bottom: 60, left: 60, right: 20 }}
        >
          {/* Legend */}
          <VictoryLegend
            x={80}
            y={0}
            orientation="horizontal"
            gutter={15}
            style={{ labels: { fontSize: 9 } }}
            data={chartDatasets.map(ds => ({
              name: ds.name,
              symbol: { fill: ds.color }
            }))}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            domain={yDomain}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Area charts for each indicator */}
          {chartDatasets.map(ds => (
            <VictoryArea
              key={ds.indicator}
              data={ds.data}
              style={{
                data: {
                  fill: ds.color,
                  fillOpacity: 0.3,
                  stroke: ds.color,
                  strokeWidth: 2
                }
              }}
              labels={({ datum }) => `${datum.indicatorName}\n${datum.period}: ${datum.value}`}
              labelComponent={
                <VictoryTooltip
                  style={{ fontSize: 9 }}
                  flyoutStyle={{
                    fill: 'white',
                    stroke: ds.color,
                    strokeWidth: 1
                  }}
                  cornerRadius={4}
                  pointerLength={6}
                />
              }
            />
          ))}
        </VictoryChart>
      </div>

      <p className="chart-description">
        Los indicadores de liquidez miden la capacidad de la institución para cumplir con
        obligaciones de corto plazo. Valores más altos indican mejor posición de liquidez.
      </p>

      <p className="chart-source">
        Fuente: Estados financieros de la institución
      </p>
    </div>
  );
};

export default Grafico11;
